import { useEffect, useRef, useState } from "react";
import { Offcanvas, Row, Col, Form, Button } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { toast } from "sonner";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import Styles from "./Profile.module.css";
import useLocalStorageState from "use-local-storage-state";
import { useNavigate } from "react-router-dom";
import { GetRequest, PutRequest } from "../../src/Utils/hooks/ApiHandler";
import { baseURL, defaultImage, imageURL } from "../Utils/constants/constant";
import { parsePhoneNumber } from "libphonenumber-js/min";
import validator from "validator";
import {
  checkDuplicate_edit,
  customStyles,
  useDebounce,
} from "../Utils/helpers/commonhelper";
import {
  IconCircleX,
  IconPlus,
  IconTrash,
  IconEdit,
  IconCheck,
  IconX,
  IconMail,
  IconPhone,
  IconUser
} from "@tabler/icons-react";
import Select from "react-select";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const contactSchema = z.object({
  salutation: z.string().default("Mr."),
  first_name: z.string().min(1, "Required"),
  last_name: z.string().min(1, "Required"),
  middle_name: z.string().optional(),
  department: z.string().optional(),
  designation: z.string().optional(),
});

const ProfileOffCanvas = ({ show, handleClose }) => {
  const [customer, setCustomer] = useLocalStorageState("customer");
  const navigate = useNavigate();

  const customerId = customer?.customer_details?.customer_id;
  const token = customer?.token?.authorization;
  const rf = customer?.token?.authentication;
  const headers = {
    authentication: rf,
    Authorization: token,
  };
  const extras = { customer, setCustomer, rf, navigate };

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid },
    watch
  } = useForm({
    resolver: zodResolver(contactSchema),
    mode: "onChange",
  });

  const [isEditMode, setIsEditMode] = useState(false);

  const [profileImage, setProfileImage] = useState(defaultImage);
  const [hasCustomProfileImage, setHasCustomProfileImage] = useState(false);

  const [emptyEmailIndices, setEmptyEmailIndices] = useState([]);
  const [emptyContactIndices, setEmptyContactIndices] = useState([]);
  const [duplicateEmailMessage, setDuplicateEmailMessage] = useState(null);
  const [duplicatePhoneMessage, setDuplicatePhoneMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [designationOptions, setDesignationOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);


  // Store form values for view mode
  const formValues = watch();
  const customer_id = customer?.customer_details?.customer_id;
  const user_login_id = customer?.customer_details?.id;

  // Emails
  const [emails, setEmails] = useState([{ email: "", email_type: "Office" }]);
  const emailOptions = [
    { value: "Office", label: "Office" },
    { value: "Personal", label: "Personal" },
  ];

  // Contacts
  const [contacts, setContacts] = useState([
    { phone: "", phone_type: "Mobile", country_code: "91", country_name: "in" },
  ]);

  const debouncedEmail = useDebounce(emails[0]?.email, 500);
  const debouncedPhone = useDebounce(contacts[0]?.phone, 500);

  const emailSectionRef = useRef(null);
  const contactSectionRef = useRef(null);

  const phoneOptions = [
    { value: "Mobile", label: "Mobile" },
    { value: "Work", label: "Work" },
    { value: "Home", label: "Home" },
    { value: "Personal", label: "Personal" },
  ];

  const validatePhoneNumber = (contact) => {
    if (!contact.phone || !contact.country_code) return false;
    const fullNumber = `+${contact.country_code}${contact.phone}`;
    try {
      const parsed = parsePhoneNumber(fullNumber);
      return parsed?.isValid() || false;
    } catch {
      return false;
    }
  };

  const convertDialCodeToISO2 = (dialCode) => {
    if (!dialCode) return "in";
    const map = {
      91: "in",
      1: "us",
      44: "gb",
      971: "ae",
      92: "pk",
      880: "bd",
    };
    return map[String(dialCode)] || "in";
  };

  // Handle contact change
  const handleContactChange = (field, value, index, details = null) => {
    const newContacts = [...contacts];
    if (field === "phone" && details) {
      newContacts[index] = {
        ...newContacts[index],
        phone: value,
        country_code: details.country_code || "91",
        country_name: details.countryName || "in"
      };
    } else {
      newContacts[index] = {
        ...newContacts[index],
        [field]: value,
      };
    }
    setContacts(newContacts);
    if (field === "phone" && value.trim() !== "") {
      setEmptyContactIndices((prev) => prev.filter((i) => i !== index));
    }
  };

  const handleDeleteContact = (index) => {
    if (contacts.length <= 1 || index === 0) {
      toast.error("Cannot delete primary contact");
      return;
    }
    const newContacts = contacts.filter((_, i) => i !== index);
    setContacts(newContacts);
    setEmptyContactIndices((prev) =>
      prev.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i))
    );
  };

  const handleAddContact = () => {
    setContacts([
      ...contacts,
      {
        phone: "",
        phone_type: "Mobile",
        country_code: "91",
        country_name: "in",
      },
    ]);
  };

  // Handle email change
  const handleEmailChange = (field, value, index) => {
    const newEmails = [...emails];
    newEmails[index] = { ...newEmails[index], [field]: value };
    setEmails(newEmails);
    if (field === "email" && value.trim() !== "") {
      setEmptyEmailIndices((prev) => prev.filter((i) => i !== index));
    }
  };

  const handleDeleteEmail = (index) => {
    if (emails.length <= 1 || index === 0) {
      toast.error("Cannot delete primary email");
      return;
    }
    const newEmails = emails.filter((_, i) => i !== index);
    setEmails(newEmails);
    setEmptyEmailIndices((prev) =>
      prev.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i))
    );
  };

  const handleAddEmail = () => {
    setEmails([
      ...emails,
      {
        email: "",
        email_type: "Office",
      },
    ]);
  };

  // Duplicate checking
  useEffect(() => {
    const checkEmail = async () => {
      if (debouncedEmail && validator.isEmail(debouncedEmail)) {
        const isEmailExisting = await checkDuplicate_edit({
          col: "email",
          user_id: user_login_id,
          validating_val: debouncedEmail,
          contact_id: "",
          checkFor: "register",
        });
        setDuplicateEmailMessage(
          isEmailExisting ? "Email already exists!" : null
        );
      } else {
        setDuplicateEmailMessage(null);
      }
    };
    checkEmail();
  }, [debouncedEmail, user_login_id]);



  useEffect(() => {
    const checkPhone = async () => {
      if (debouncedPhone && contacts[0]?.country_code) {
        const fullPhone = `${contacts[0].country_code}${debouncedPhone}`;
        const isPhoneExisting = await checkDuplicate_edit({
          col: "phone",
          user_id: user_login_id,
          validating_val: fullPhone,
          contact_id: "",
          checkFor: "register",
        });
        setDuplicatePhoneMessage(
          isPhoneExisting ? "Phone number already exists!" : null
        );
      } else {
        setDuplicatePhoneMessage(null);
      }
    };
    checkPhone();
  }, [debouncedPhone, contacts[0]?.country_code, user_login_id]);

  useEffect(() => {
    if (!show) {
      setIsEditMode(false);
      return;
    }
    fetchProfileDetails();
  }, [show]);

  const fetchProfileDetails = async () => {
    try {
      const res = await GetRequest(
        `profile/profileDetails/${customer_id}/${user_login_id}`,
        { headers },
        extras
      );
      if (res.err) return toast.error("Error Fetching Data");

      const d = res.data?.[0];
      reset({
        salutation: d.salutation || "Mr.",
        first_name: d.first_name || "",
        last_name: d.last_name || "",
        middle_name: d.middle_name || "",
        department: d.department_id || "",
        designation: d.designation_id || "",
      });

      let parsedEmails = [];
      if (d.user_email) {
        try {
          parsedEmails = JSON.parse(d.user_email);
        } catch {
          parsedEmails = [];
        }
      }
      if (parsedEmails.length === 0 && d.email) {
        parsedEmails = [{ email: d.email, email_type: "Office" }];
      }
      setEmails(parsedEmails.length ? parsedEmails : [{ email: "", email_type: "Office" }]);

      let parsedPhones = [];
      if (d.user_phone) {
        try {
          parsedPhones = JSON.parse(d.user_phone);
        } catch {
          parsedPhones = [];
        }
      }
      if (parsedPhones.length === 0 && d.phone) {
        const ccode = (d.country_code || "91").replace(/^\+/, "");
        parsedPhones = [{
          phone: d.phone.replace(/^\+?\d+/, d.phone),
          phone_type: "Mobile",
          country_code: ccode,
          country_name: convertDialCodeToISO2(ccode),
        }];
      }
      const formattedPhones = parsedPhones.map((p) => {
        const ccode = (p.country_code || "91").replace(/^\+/, "");
        return {
          phone: String(p.phone),
          phone_type: p.phone_type || "Mobile",
          country_code: ccode,
          country_name: p.country_name || convertDialCodeToISO2(ccode),
        };
      });
      setContacts(formattedPhones);

      const image = d.profile_img || defaultImage;

      setProfileImage(image);
      setHasCustomProfileImage(Boolean(d.profile_img));

    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Failed to load profile data");
    }
  };

  const fetchDepartmentDetails = async () => {
    try {
      const res = await GetRequest(`common/fetchDepartment/${customer_id}`, { headers }, extras);
      if (res.data) {
        const formatted = res.data.map(dept => ({
          value: dept.id,
          label: dept.department_name
        }));
        setDepartmentOptions(formatted);
      }
    } catch (err) {
      toast.error("Failed to load departments");
    }
  };

  const fetchDesignationDetails = async () => {
    try {
      const res = await GetRequest(`common/fetchDesignation/${customer_id}`, { headers }, extras);
      if (res.data) {
        const formatted = res.data.map(desg => ({
          value: desg.id,
          label: desg.designation_name
        }));
        setDesignationOptions(formatted);
      }
    } catch (err) {
      toast.error("Failed to load designations");
    }
  };


  useEffect(() => {
    if (show) {
      fetchDepartmentDetails();
      fetchDesignationDetails();
    }
  }, [show]);


  const validateForm = () => {
    const newEmptyEmailIndices = emails.reduce((acc, email, index) => {
      if (!email.email || email.email.trim() === "") {
        acc.push(index);
      }
      return acc;
    }, []);

    const newEmptyContactIndices = contacts.reduce((acc, contact, index) => {
      if (!contact.phone || contact.phone.trim() === "") {
        acc.push(index);
      }
      return acc;
    }, []);

    setEmptyEmailIndices(newEmptyEmailIndices);
    setEmptyContactIndices(newEmptyContactIndices);

    if (newEmptyEmailIndices.length > 0) {
      emailSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      toast.error("Please fill all email fields");
      return false;
    }

    if (newEmptyContactIndices.length > 0) {
      contactSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      toast.error("Please fill all contact fields");
      return false;
    }

    const invalidEmail = emails.find(
      (email) => email.email.trim() !== "" && !validator.isEmail(email.email)
    );
    if (invalidEmail) {
      emailSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      toast.error("Invalid email format");
      return false;
    }

    const invalidContact = contacts.find(
      (contact) =>
        contact.phone.trim() !== "" &&
        !validatePhoneNumber(contact)
    );
    if (invalidContact) {
      contactSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      toast.error("Invalid phone number");
      return false;
    }

    return true;
  };

  // Form submit handler
  const handleFormSubmit = async (data) => {
    if (!validateForm()) return;
    if (duplicateEmailMessage || duplicatePhoneMessage) {
      toast.error("Please fix duplicate errors before submitting");
      return;
    }

    setIsSubmitting(true);

    try {
      const dataToSend = {
        ...data,
        customer_id: customer_id,
        user_id: user_login_id,
        email: emails[0]?.email || "",
        phone: contacts[0]?.phone || "",
        phone_type: contacts[0]?.phone_type || "Mobile",
        country_code: contacts[0]?.country_code || "91",
        user_email: emails.filter((email) => email.email.trim() !== ""),
        user_phone: contacts
          .filter((contact) => contact.phone.trim() !== "")
          .map((contact) => ({
            phone: contact.phone,
            phone_type: contact.phone_type,
            country_code: contact.country_code,
            country_name: contact.country_name,
          })),
      };


      const res = await PutRequest(
        `profile/updateProfile/`,
        dataToSend,
        { headers },
        extras
      );


      if (res.status === "fail" || !res.status) {
        toast.error(res.message || "Failed to update profile");
        return;
      }

      if (res.status === "success") {
        toast.success(res.message || "Profile updated successfully!");
        await fetchProfileDetails();
        setIsEditMode(false);
      } else {
        toast.error(res.message || "Failed to update profile");
      }
    } catch (error) {
      console.error("Submit error:", error);
      if (error.message?.includes("token") || error.message?.includes("auth") || error.status === 401) {
        toast.error("Session expired. Please login again.");
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        toast.error("An error occurred while updating profile");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    fetchProfileDetails();
  };

  // Helper function to get full name
  const getFullName = () => {
    const { salutation = "Mr.", first_name = "", middle_name = "", last_name = "" } = formValues;
    return `${salutation} ${first_name} ${middle_name ? middle_name + ' ' : ''}${last_name}`.trim();
  };


  const customStyle = {
    control: (base) => ({
      ...base,
      fontSize: 14,
      minHeight: 43,
      height: 43,
      borderRadius: "6px"
    }),
    valueContainer: (base) => ({
      ...base,
      fontSize: 14,
      height: 43,
      padding: "0 5px",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      fontSize: 14,
      height: 43,
    }),
  };

  return (
    <Offcanvas
      show={show}
      onHide={handleClose}
      className="createContactOffcanvas"
      placement="end"
      style={{ width: "600px" }}
    >
      {/* <Offcanvas.Header closeButton className={Styles.offcanvasHeader}>
        <Offcanvas.Title className={Styles.offcanvasTitle}>
          Account Settings
        </Offcanvas.Title>
      </Offcanvas.Header> */}
      <Offcanvas.Header
        closeButton
        className="border-bottom border-dashed pt-3"
      >
        <div className="d-flex align-items-center gap-2">
          <div className="pt-0 ps-4">
            <Offcanvas.Title
              style={{ color: "#0791ff" }}
              className="addEnquiryHead"
            >
              {" "}
              Account Settings
            </Offcanvas.Title>
            <p
              style={{ fontSize: "14px", color: "#b26792", lineHeight: 1.1 }}
              className=" text-secondary text-nowrap mb-0"
            >
              Manage your personal information, account preferences, and settings to keep your profile up-to-date.
            </p>
          </div>
        </div>
      </Offcanvas.Header>


      <Offcanvas.Body className={Styles.profileBody}>
        {/* PROFILE IMAGE */}
        <div className={Styles.profilePictureSection}>
          <div className={Styles.profilePictureLeft}>
            <img
              src={
                profileImage.startsWith("data:") || profileImage.startsWith("http") || profileImage.startsWith("blob:")
                  ? profileImage
                  : `${imageURL}${profileImage}`
              }
              alt="Profile"
              className={Styles.profileAvatar}
            // onError={(e) => {
            //   e.target.src = defaultImage;
            // }}
            />
            <div className={Styles.profilePictureInfo}>
              <h3 className={Styles.profilePictureTitle}>Profile picture</h3>
              <p className={Styles.profilePictureHint}>PNG, JPEG under 15MB</p>
            </div>
          </div>

          <div className={Styles.profilePictureActions}>
            <label className={Styles.btnUpload}>
              Upload new picture
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;

                  if (file.size > 15 * 1024 * 1024) {
                    toast.error("File size must be less than 15MB");
                    return;
                  }

                  const reader = new FileReader();
                  reader.onloadend = () => setProfileImage(reader.result);
                  reader.readAsDataURL(file);

                  const formData = new FormData();
                  formData.append("user_id", user_login_id);
                  formData.append("customer_id", customer_id);
                  formData.append("logo", file);

                  try {
                    const res = await fetch(
                      `${baseURL}profile/updateProfileImage/`,
                      {
                        method: "POST",
                        headers: {
                          Authorization: token,
                          Authentication: rf,
                        },
                        body: formData,
                      }
                    );

                    const result = await res.json();

                    if (result.status === "success") {
                      setHasCustomProfileImage(true);
                      await fetchProfileDetails();
                      toast.success("Profile image updated successfully!");
                    } else {
                      toast.error(result.message || "Failed to upload image");
                    }
                  } catch (error) {
                    console.error("Upload failed:", error);
                    toast.error("Failed to upload image");
                  }
                }}
              />
            </label>
            <button
              className={Styles.btnDeleteImg}
              disabled={!hasCustomProfileImage}
              onClick={async () => {
                try {
                  const formData = new FormData();
                  formData.append("user_id", user_login_id);
                  formData.append("customer_id", customer_id);
                  formData.append("logo", "");

                  const res = await fetch(
                    `${baseURL}profile/updateProfileImage/`,
                    {
                      method: "POST",
                      headers: {
                        Authorization: token,
                        Authentication: rf,
                      },
                      body: formData,
                    }
                  );

                  const result = await res.json();
                  if (result.status === "success") {
                    setProfileImage(defaultImage);
                    setHasCustomProfileImage(false);
                    await fetchProfileDetails();
                    toast.success("Profile image removed");
                  }
                } catch (error) {
                  toast.error("Failed to remove image");
                }
              }}
            >
              Delete
            </button>
          </div>
        </div>

        <hr className={Styles.divider} />

        {/* FORM START */}
        <Form onSubmit={handleSubmit(handleFormSubmit)}>
          {!isEditMode ? (
            /* VIEW MODE - Clean Professional Display */
            <div className={Styles.viewMode} style={{ marginBottom: "80px" }}>
              {/* NAME SECTION - View Mode */}
              <div className={Styles.sectionHeader}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <IconUser size={18} className="text-gray-600" />
                  <h3 className={Styles.sectionTitle}>Personal Information</h3>
                </div>
                <p className={Styles.sectionSubtitle}>
                  Your personal details and identification information.
                </p>
              </div>

              <div className={Styles.viewGrid}>
                <div className={Styles.viewRow}>
                  <span className={Styles.viewLabel}>Salutation:</span>
                  <span className={Styles.viewValue}>{formValues.salutation || "Mr."}</span>
                </div>
                <div className={Styles.viewRow}>
                  <span className={Styles.viewLabel}>First Name:</span>
                  <span className={Styles.viewValue}>{formValues.first_name || "-"}</span>
                </div>
                {formValues.middle_name && (
                  <div className={Styles.viewRow}>
                    <span className={Styles.viewLabel}>Middle Name:</span>
                    <span className={Styles.viewValue}>{formValues.middle_name}</span>
                  </div>
                )}
                <div className={Styles.viewRow}>
                  <span className={Styles.viewLabel}>Last Name:</span>
                  <span className={Styles.viewValue}>{formValues.last_name || "-"}</span>
                </div>
                <div className={Styles.viewRow}>
                  <span className={Styles.viewLabel}>Department:</span>
                  <span className={Styles.viewValue}>
                    {departmentOptions.find(opt => String(opt.value) === String(formValues.department))?.label || "-"}
                  </span>
                </div>

                <div className={Styles.viewRow}>
                  <span className={Styles.viewLabel}>Designation:</span>
                  <span className={Styles.viewValue}>
                    {designationOptions.find(opt => String(opt.value) === String(formValues.designation))?.label || "-"}
                  </span>
                </div>
                {/* <div className={Styles.viewRow}>
                  <span className={Styles.viewLabel}>Full Name:</span>
                  <span className={Styles.viewValue}>{getFullName()}</span>
                </div> */}
              </div>

              <hr className={Styles.divider} />

              {/* EMAIL SECTION - View Mode */}
              <div ref={emailSectionRef} className={Styles.sectionHeader}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <IconMail size={18} className="text-gray-600" />
                  <h3 className={Styles.sectionTitle}>Contact Email</h3>
                </div>
                <p className={Styles.sectionSubtitle}>
                  Email addresses for communication and invoices.
                </p>
              </div>

              {emails.length > 0 && emails.some(e => e.email) ? (
                <div className={Styles.viewGrid}>
                  {emails.map((email, index) => (
                    <div key={index} className={Styles.viewRow}>
                      <div className="d-flex align-items-center justify-content-between w-100">
                        <div className="d-flex flex-column">
                          <span className={Styles.viewLabel}>
                            {index === 0 ? "Primary Email" : `Additional Email ${index}`}
                            <span className={Styles.typeBadge} style={{
                              backgroundColor: email.email_type === 'Office' ? '#3B82F6' : '#10B981',
                              marginLeft: '8px'
                            }}>
                              {email.email_type}
                            </span>
                          </span>
                          <span className={Styles.viewValue}>
                            <a href={`mailto:${email.email}`} className="text-decoration-none text-primary">
                              {email.email}
                            </a>
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={Styles.emptyState}>
                  <IconMail size={24} className="text-gray-400 mb-2" />
                  <p className="text-gray-500 mb-0">No email addresses added yet</p>
                </div>
              )}

              <hr className={Styles.divider} />

              {/* PHONE SECTION - View Mode */}
              <div ref={contactSectionRef} className={Styles.sectionHeader}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <IconPhone size={18} className="text-gray-600" />
                  <h3 className={Styles.sectionTitle}>Contact Phone</h3>
                </div>
                <p className={Styles.sectionSubtitle}>
                  Phone numbers for communication and verification.
                </p>
              </div>

              {contacts.length > 0 && contacts.some(c => c.phone) ? (
                <div className={Styles.viewGrid}>
                  {contacts.map((contact, index) => (
                    <div key={index} className={Styles.viewRow}>
                      <div className="d-flex align-items-center justify-content-between w-100">
                        <div className="d-flex flex-column">
                          <span className={Styles.viewLabel}>
                            {index === 0 ? "Primary Phone" : `Additional Phone ${index}`}
                            <span className={Styles.typeBadge} style={{
                              backgroundColor: contact.phone_type === 'Mobile' ? '#8B5CF6' :
                                contact.phone_type === 'Work' ? '#F59E0B' :
                                  contact.phone_type === 'Home' ? '#EF4444' : '#10B981',
                              marginLeft: '8px'
                            }}>
                              {contact.phone_type}
                            </span>
                          </span>
                          <span className={Styles.viewValue}>
                            <a href={`tel:+${contact.country_code}${contact.phone}`} className="text-decoration-none text-primary">
                              +{contact.country_code} {contact.phone}
                            </a>
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={Styles.emptyState}>
                  <IconPhone size={24} className="text-gray-400 mb-2" />
                  <p className="text-gray-500 mb-0">No phone numbers added yet</p>
                </div>
              )}

              <div className={Styles.additionalInfo}>
                <p className={Styles.infoText}>
                  <strong>Note:</strong> Primary email and phone are used for system notifications and account recovery.
                </p>
              </div>
            </div>
          ) : (
            /* EDIT MODE - Form View */
            <>
              {/* FULL NAME SECTION - Edit Mode */}
              <div className={Styles.formSection}>
                <h3 className={Styles.formSectionTitle}>Full name</h3>
                <div className={Styles.formRow}>
                  <div className={Styles.formGroup}>
                    <label className={Styles.formLabel}>Salutation</label>
                    <Controller
                      name="salutation"
                      control={control}
                      render={({ field }) => (
                        <Form.Select
                          {...field}
                          className={Styles.formInput}
                          style={{ backgroundColor: "#fff" }}
                        >
                          <option value="Mr.">Mr.</option>
                          <option value="Mrs.">Mrs.</option>
                          <option value="Miss">Miss</option>
                        </Form.Select>
                      )}
                    />
                  </div>
                  <div className={Styles.formGroup}>
                    <label className={Styles.formLabel}>
                      First name <span className="text-danger">*</span>
                    </label>
                    <Controller
                      name="first_name"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="text"
                          className={`${Styles.formInput} ${errors.first_name ? Styles.error : ''}`}
                          placeholder="Enter First name"
                        />
                      )}
                    />
                    {errors.first_name && (
                      <div className={Styles.errorMessage}>{errors.first_name.message}</div>
                    )}
                  </div>
                </div>
                <div className={Styles.formRow}>
                  <div className={Styles.formGroup}>
                    <label className={Styles.formLabel}>Middle name</label>
                    <Controller
                      name="middle_name"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="text"
                          className={Styles.formInput}
                          placeholder="Enter Middle name"
                        />
                      )}
                    />
                  </div>
                  <div className={Styles.formGroup}>
                    <label className={Styles.formLabel}>
                      Last name <span className="text-danger">*</span>
                    </label>
                    <Controller
                      name="last_name"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="text"
                          className={`${Styles.formInput} ${errors.last_name ? Styles.error : ''}`}
                          placeholder="Enter Last name"
                        />
                      )}
                    />
                    {errors.last_name && (
                      <div className={Styles.errorMessage}>{errors.last_name.message}</div>
                    )}
                  </div>
                </div>

                <hr className={Styles.divider} />

                <div className={Styles.formRow}>
                  <div className={Styles.formGroup}>
                    <label className={Styles.formLabel}>Department</label>
                    <Controller
                      name="department"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={departmentOptions}
                          value={departmentOptions.find((opt) => opt.value === field.value) || null}
                          onChange={(val) => field.onChange(val ? val.value : "")}
                          styles={customStyle}
                          placeholder="Select Department"
                          isClearable
                        />
                      )}
                    />
                  </div>


                  <div className={Styles.formGroup}>
                    <label className={Styles.formLabel}>Designation</label>
                    <Controller
                      name="designation"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={designationOptions}
                          value={designationOptions.find((opt) => opt.value === field.value) || null}
                          onChange={(val) => field.onChange(val ? val.value : "")}
                          styles={customStyle}
                          placeholder="Select Designation"
                          isClearable
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              <hr className={Styles.divider} />

              {/* EMAIL SECTION - Edit Mode */}
              <div ref={emailSectionRef} className={Styles.formSection}>
                <h3 className={Styles.formSectionTitle}>Contact email</h3>
                <p className={Styles.formSectionSubtitle}>
                  Manage your accounts email address for the invoices.
                </p>

                {emails.map((email, index) => {
                  const isDuplicate = emails.filter(
                    (e, i) => e.email === email.email && email.email !== "" && i !== index
                  ).length > 0;
                  const isEmailEmpty = emptyEmailIndices?.includes(index);
                  const isLastEmail = index === emails.length - 1;

                  return (
                    <div key={index} className={Styles.fieldRowContainer}>
                      <div className={Styles.fieldInputArea}>
                        <div className="d-flex align-items-start gap-3 w-100">
                          {/* Email Type Dropdown */}
                          <div style={{ width: "140px", flexShrink: 0 }}>
                            <Select
                              // classNamePrefix="react-select"
                              styles={customStyle}


                              isSearchable={false}
                              value={emailOptions.find(
                                (opt) => opt.value === (email.email_type || "Office")
                              )}
                              options={emailOptions}
                              onChange={(selectedOption) =>
                                handleEmailChange(
                                  "email_type",
                                  selectedOption?.value || "Office",
                                  index
                                )
                              }
                            />
                          </div>

                          {/* Email Input */}
                          <div style={{ width: "360px" }}>
                            <input
                              type="email"
                              value={email.email}
                              onChange={(e) =>
                                handleEmailChange("email", e.target.value, index)
                              }
                              placeholder="Enter email address"
                              className={`${Styles.formInput} ${isDuplicate ? Styles.error : ''}`}
                              style={{ width: "100%" }}
                            />
                          </div>

                          {/* Action Buttons */}
                          <div className="d-flex align-items-center gap-2" style={{ marginTop: "0" }}>
                            {/* Delete Button - Not for first email */}
                            {emails.length > 1 && index > 0 && (
                              <button
                                type="button"
                                className={Styles.btnDeleteField}
                                onClick={() => handleDeleteEmail(index)}
                                title="Delete email"
                              >
                                <IconTrash size={16} />
                              </button>
                            )}

                            {/* Add Button - Only on last row */}
                            {isLastEmail && (
                              <button
                                type="button"
                                className={Styles.btnAdd}
                                onClick={handleAddEmail}
                              >
                                <IconPlus size={16} />
                                Add another email
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Error Messages */}
                        {isDuplicate && (
                          <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                            Duplicate email
                          </div>
                        )}
                        {isEmailEmpty && (
                          <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                            Email is Required!
                          </div>
                        )}
                        {email.email && email.email?.trim() !== "" && !validator.isEmail(email.email) && (
                          <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                            Invalid email format
                          </div>
                        )}
                        {duplicateEmailMessage && index === 0 && (
                          <div className="text-danger mt-1 d-flex align-items-center gap-1" style={{ fontSize: "12px" }}>
                            <IconCircleX size={14} />
                            {duplicateEmailMessage}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <hr className={Styles.divider} />

              {/* PHONE SECTION - Edit Mode */}
              <div ref={contactSectionRef} className={Styles.formSection}>
                <h3 className={Styles.formSectionTitle}>Contact phone</h3>
                <p className={Styles.formSectionSubtitle}>
                  Manage your phone numbers for communication.
                </p>

                {contacts.map((contact, index) => {
                  const isDuplicate = contacts.filter(
                    (c, i) => c.phone === contact.phone &&
                      c.country_code === contact.country_code &&
                      contact.phone !== "" && i !== index
                  ).length > 0;
                  const isContactEmpty = emptyContactIndices?.includes(index);
                  const isValidContact = validatePhoneNumber(contact);
                  const isLastContact = index === contacts.length - 1;

                  return (
                    <div key={index} className={Styles.fieldRowContainer}>
                      <div className={Styles.fieldInputArea}>
                        <div className="d-flex align-items-start gap-3 w-100">
                          {/* Phone Type Dropdown */}
                          <div style={{ width: "140px", flexShrink: 0 }}>
                            <Select

                              styles={customStyle}
                              value={phoneOptions.find(
                                (opt) => opt.value === (contact.phone_type || "Mobile")
                              )}
                              options={phoneOptions}
                              onChange={(selectedOption) =>
                                handleContactChange(
                                  "phone_type",
                                  selectedOption?.value || "Mobile",
                                  index
                                )
                              }
                            />
                          </div>

                          {/* Phone Input */}
                          <div style={{ width: "360px" }}>
                            <div className={Styles.phoneInputWrapper}>

                              <PhoneInput
                                className={Styles.phoneInputWrapper}
                                country={contact.country_name || "in"}
                                countryCodeEditable={false}
                                inputStyle={{
                                  width: "100%",
                                  height: "44px",
                                  border: `${isDuplicate || !isValidContact ? "1px solid #dc3545" : "1px solid #d1d5db"}`,
                                  borderRadius: "8px",
                                }}
                                containerStyle={{
                                  border: "none",
                                }}
                                value={`+${contact.country_code}${contact.phone}`}
                                onChange={(value, country, e, rawInputValue) => {
                                  const dial = country.dialCode;
                                  const raw = rawInputValue.replace(/\D/g, "").slice(dial.length);
                                  handleContactChange(
                                    "phone",
                                    raw,
                                    index,
                                    {
                                      country_code: dial,
                                      country_name: country.countryCode,
                                    }
                                  );
                                }}
                              />
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="d-flex align-items-center gap-2" style={{ marginTop: "0" }}>
                            {/* Delete Button - Not for first contact */}
                            {contacts.length > 1 && index > 0 && (
                              <button
                                type="button"
                                className={Styles.btnDeleteField}
                                onClick={() => handleDeleteContact(index)}
                                title="Delete contact"
                              >
                                <IconTrash size={16} />
                              </button>
                            )}

                            {/* Add Button - Only on last row */}
                            {isLastContact && (
                              <button
                                type="button"
                                className={Styles.btnAdd}
                                onClick={handleAddContact}
                              >
                                <IconPlus size={16} />
                                Add another phone
                              </button>
                            )}
                          </div>
                        </div>


                        {/* Error Messages */}
                        {isDuplicate && (
                          <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                            Duplicate contact!
                          </div>
                        )}
                        {isContactEmpty && (
                          <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                            Contact is Required!
                          </div>
                        )}
                        {contact.phone && contact.phone?.trim() !== "" && !isValidContact && (
                          <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                            Phone is Invalid!
                          </div>
                        )}
                        {duplicatePhoneMessage && index === 0 && (
                          <div className="text-danger mt-1 d-flex align-items-center gap-1" style={{ fontSize: "12px" }}>
                            <IconCircleX size={14} />
                            {duplicatePhoneMessage}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* FORM FOOTER */}
          <div
            style={{
              position: "fixed",
              width: "60%",
              bottom: 0,
              height: "74px",
              zIndex: 50,
              right: 0,
              background: "#fefefe",
              paddingRight: "20px",
              borderTop: "1px solid #c4ebf7",
            }}
          >
            <div className="d-flex justify-content-end gap-2 pt-4">
              {!isEditMode ? (
                <Button
                  className="btn btn-default"
                  onClick={() => setIsEditMode(true)}
                  style={{ minWidth: "140px" }}
                >
                  <IconEdit size={18} style={{ marginRight: "8px" }} />
                  Edit Profile
                </Button>

              ) : (
                <>
                  <Button
                    className="btn-default-outline"
                    onClick={handleCancelEdit}
                    disabled={isSubmitting}
                    style={{ minWidth: "100px" }}
                  >
                    <IconX size={18} style={{ marginRight: "6px" }} />
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="btn btn-default"
                    disabled={
                      isSubmitting ||
                      !!duplicateEmailMessage ||
                      !!duplicatePhoneMessage ||
                      !isValid
                    }
                    style={{ minWidth: "140px" }}
                  >
                    {isSubmitting ? "Saving..." : "Save Changes"}
                  </Button>
                </>
              )}</div>
          </div>
        </Form>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default ProfileOffCanvas;